import Color from 'color';
import { twipsToPx } from '@tencent/alloy-editor';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { Quadtree } from '@src/slide/infrastructure/module/independent/quadtree/quadtree';
import { Rectangle } from '@src/slide/infrastructure/module/independent/quadtree/rectangle';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import {
    IElementNode,
    ITextElementBoxNode,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer.interface';
import { FillType, IFillProperty, IImageFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { ISolidFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/solid-fill';
import {
    UnknownColor,
    ITextColorBack,
    ISlideBack,
    IElementBack,
    ITextElementBack,
    ColorBackType,
    IColorAnalysisResult,
    IColorAnalysisTask,
} from '@src/slide/application/service/slide-rating-and-improve/base/color-base-analyzer.interface';
import { getFrameAfterRotate, rotateRectWithPoint } from '@src/slide/infrastructure/module/independent/graphic';
import {
    getOffsetOfTextBox,
    isStringVisible,
} from '@src/slide/application/service/slide-rating-and-improve/text/calculate/text-box-calculate';
import { checkRectIntersection } from '@src/slide/application/service/slide-rating-and-improve/text/calculate/quadtree-calculate';
import { TextColorContrastResult } from '../color/result/text-color-contrast-result';
import { getImageTextColorContrast } from './image-color-utils';

/**
 * 颜色对比度基础分析器
 * 排版美化和机器审核在此基础上异化
 */
export class ColorBaseAnalyzer {
    protected presentation: IPresentationModel;
    protected colorQuadtree: Quadtree<Rectangle<ITextColorBack>> | undefined;
    protected analysisImage = false;
    private tasks: IColorAnalysisTask[] = [];

    public resetWithPresentation(presentation?: IPresentationModel) {
        this.tasks = [];
        this.colorQuadtree = undefined;
        if (presentation) {
            this.presentation = presentation;
        }
    }

    public analysisSlide(slideModel: ISlideModel) {
        this.initSlideQuadTree(slideModel);
    }

    // eslint-disable-next-line complexity
    public analysisElement(elementNode: IElementNode) {
        const elementModel = elementNode.element;
        const frame = elementModel.getFrame().frameOnSlide();
        let bounds = frame;
        let hasRotate = false;
        if (frame.rotate !== undefined && frame.rotate !== 0) {
            hasRotate = true;
            bounds = getFrameAfterRotate(frame, frame.rotate);
        }
        const elementColorBack: IElementBack = {
            type: ColorBackType.ELEMENT,
            elementNode,
            color: UnknownColor,
        };
        const elementColorRect: Rectangle<IElementBack> = new Rectangle({
            x: bounds.left, y: bounds.top,
            width: bounds.width,
            height: bounds.height,
            data: elementColorBack,
        });
        if (elementModel.type === UIModelType.SHAPE) {
            if (elementModel.getBackgroundFill().getShownFillType() === FillType.ImageFill) {
                const imageInfo = elementModel.getBackgroundFill().getCurrentFill()?.fillInfo;
                if (imageInfo && !hasRotate) {
                    elementColorBack.image = imageInfo as IImageFill;
                }
                this.colorQuadtree?.insert(elementColorRect);
            } else {
                let color = this.getColorFromFill(elementModel.getBackgroundFill());
                if (color) {
                    if (hasRotate) {
                        color = UnknownColor;
                    }
                    elementColorBack.color = color;
                    this.colorQuadtree?.insert(elementColorRect);
                }
            }
        } else if (elementModel.type === UIModelType.IMAGE
            || elementModel.type === UIModelType.TABLE
            || elementModel.type === UIModelType.CHART
        ) {
            this.colorQuadtree?.insert(elementColorRect);
        }
    }

    // eslint-disable-next-line complexity
    public analysisTextElementBox(textElementBoxNode: ITextElementBoxNode) {
        // 字符不可见，不用比了
        if (!isStringVisible(textElementBoxNode.textBox.text)) return;
        const textColor = new Color(textElementBoxNode.textBox.textStyle.textColor);
        if (textElementBoxNode.element.isDatePl
            || textElementBoxNode.element.isFooterPl
            || textElementBoxNode.element.isSlideNumPl
            || textElementBoxNode.element.type === UIModelType.TABLE
            || textElementBoxNode.element.type === UIModelType.CHART
        ) return;
        // 非纯色填充暂时不支持
        if (!textColor) return;
        const textBack = textElementBoxNode.textBox.textStyle.textBackground;
        if (textBack && textBack !== 'transparent') {
            const color = new Color(textBack);
            const textBackRect: ITextElementBack = {
                type: ColorBackType.TEXT_BACK,
                textElementNode: textElementBoxNode,
                color,
            };
            // 有文字背景，直接对比文字和背景
            this.addTask(textColor, new Color(textBack), textElementBoxNode, textBackRect);
        } else {
            if (textElementBoxNode.element.type === UIModelType.SHAPE) {
                // 没有文字背景，先取model的填充和文字进行对比
                if (textElementBoxNode.element.getBackgroundFill().getShownFillType() === FillType.SolidFill) {
                    const shapeColor = this.getColorFromFill(textElementBoxNode.element.getBackgroundFill()) as Color;
                    const elementBackRect: IElementBack = {
                        type: ColorBackType.ELEMENT,
                        elementNode: textElementBoxNode,
                        color: shapeColor,
                    };
                    this.addTask(textColor, shapeColor, textElementBoxNode, elementBackRect);
                } else if (textElementBoxNode.element.getBackgroundFill().getShownFillType() === FillType.ImageFill) {
                    const imageInfo = textElementBoxNode.element.getBackgroundFill().getCurrentFill()?.fillInfo;
                    if (imageInfo) {
                        const imageBack: IElementBack = {
                            type: ColorBackType.ELEMENT,
                            color: UnknownColor,
                            elementNode: textElementBoxNode,
                            image: imageInfo as IImageFill,
                        };
                        this.addTask(textColor, UnknownColor, textElementBoxNode, imageBack);
                    }
                } else {
                    const { rect } = textElementBoxNode.textBox;
                    const offset = getOffsetOfTextBox(textElementBoxNode.textBox);
                    const elementModel = textElementBoxNode.element;
                    const elementFrame = elementModel.getFrame().frameOnSlide();
                    let boxBounds = {
                        left: twipsToPx(offset.x) + elementFrame.left,
                        top: twipsToPx(offset.y) + elementFrame.top,
                        width: twipsToPx(rect.width),
                        height: twipsToPx(rect.height),
                    };
                    if (elementFrame.rotate !== undefined && elementFrame.rotate !== 0) {
                        const center = {
                            x: elementFrame.left + (elementFrame.width / 2),
                            y: elementFrame.top + (elementFrame.height / 2),
                        };
                        boxBounds = rotateRectWithPoint(boxBounds, center, elementFrame.rotate);
                    }
                    const textBound = new Rectangle({
                        x: boxBounds.left,
                        y: boxBounds.top,
                        width: boxBounds.width,
                        height: boxBounds.height,
                    });
                    const rects = this.colorQuadtree?.retrieve(textBound) as Array<Rectangle<ITextColorBack>>;
                    this.checkTextAndBackRects(textBound, textColor, rects, textElementBoxNode);
                }
            }
        }
    }

    // 计算对比度数值结果，一般在文档分析完毕时进行
    protected calculateResult(): Promise<IColorAnalysisResult>[] {
        const results: Promise<IColorAnalysisResult>[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const task of this.tasks) {
            const { boxNode, textColor, textBack } = task;
            if (task.textBack.image && this.analysisImage) {
                const result = getImageTextColorContrast(task);
                if (result) {
                    results.push(result);
                }
                continue;
            }
            if (textBack.color === UnknownColor) continue;
            const contrast = TextColorContrastResult.getColorsContrast(textColor, textBack.color);
            const pxScale = window.screen.width / (this.presentation.props.slideWidth / window.devicePixelRatio);
            const fontSize = boxNode.textBox.textStyle.fontSize / 30 * pxScale;
            const { rule, ratio } = TextColorContrastResult.getCompareRatioAndRule(fontSize);
            results.push(Promise.resolve({
                boxNode,
                textColor,
                textFontSize: fontSize,
                textBack,
                contrastRule: rule,
                contrast,
                ratio,
            }));
        }
        return results;
    }

    private addTask(
        textColor: Color | typeof UnknownColor, backColor: Color | typeof UnknownColor,
        textElementBoxNode: ITextElementBoxNode, textBack: ITextColorBack,
    ) {
        if (textColor === UnknownColor)  return;
        if (backColor === UnknownColor && !textBack.image)  return;
        this.tasks.push({
            boxNode: textElementBoxNode,
            textColor,
            textBack: { ...textBack, color: backColor as Color },
        });
    }

    /**
     * 分析某页数据，初始化背景
     * @param slideModel
     */
    private initSlideQuadTree(slideModel: ISlideModel) {
        const bounds = {
            x: 0,
            y: 0,
            width: this.presentation.props.slideWidth,
            height: this.presentation.props.slideHeight,
        };
        this.colorQuadtree = new Quadtree(bounds);
        if (slideModel.getBackgroundFill().getShownFillType() === FillType.ImageFill) {
            const imageInfo = slideModel.getBackgroundFill().getCurrentFill()?.fillInfo;
            const slideBackColor: ISlideBack = {
                type: ColorBackType.SLIDE_BACKGROUND,
                slideModel,
                color: UnknownColor,
                image: imageInfo as IImageFill,
            };
            const slideBackColorRect: Rectangle<ISlideBack> = new Rectangle({
                x: 0,
                y: 0,
                width: this.presentation.props.slideWidth,
                height: this.presentation.props.slideHeight,
                data: slideBackColor,
            });
            this.colorQuadtree.insert(slideBackColorRect);
            return;
        }
        const backColor = this.getColorFromFill(slideModel.getBackgroundFill());
        if (!backColor) return;
        const slideBackColor: ISlideBack = {
            type: ColorBackType.SLIDE_BACKGROUND,
            slideModel,
            color: backColor,
        };
        const slideBackColorRect: Rectangle<ISlideBack> = new Rectangle({
            x: 0,
            y: 0,
            width: this.presentation.props.slideWidth,
            height: this.presentation.props.slideHeight,
            data: slideBackColor,
        });
        this.colorQuadtree.insert(slideBackColorRect);
    }

    private checkTextAndBackRects(
        textBound: Rectangle,
        textColor: Color,
        rects: Array<Rectangle<ITextColorBack>>,
        textElementBoxNode: ITextElementBoxNode,
    ) {
        const sortedRects = rects.sort((aRect, bRect) => {
            const a = aRect.data as ITextColorBack;
            const b = bRect.data as ITextColorBack;
            if (a.type === b.type) {
                if (a.type === ColorBackType.ELEMENT) {
                    const pathA = (a as IElementBack).elementNode.path;
                    const pathB = (b as IElementBack).elementNode.path;
                    const pathBLength = pathB.length;
                    for (let i = 0; i < pathA.length; i++) {
                        if (pathBLength < i) {
                            return 1;
                        }
                        if (pathA[i] !== pathB[i]) {
                            return pathB[i] - pathA[i];
                        }
                    }
                }
                return b.type - a.type;
            }
            return b.type - a.type;
        });
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < sortedRects.length; i++) {
            if (checkRectIntersection(textBound, sortedRects[i])) {
                const xLeft = Math.max(textBound.x, sortedRects[i].x);
                const yTop = Math.max(textBound.y, sortedRects[i].y);
                const xRight = Math.min(textBound.x + textBound.width, sortedRects[i].x + sortedRects[i].width);
                const yBottom = Math.min(textBound.y + textBound.height, sortedRects[i].y + sortedRects[i].height);

                // Check if there is an intersection
                if (xLeft < xRight && yTop < yBottom) {
                    // Calculate the area of the intersection rectangle
                    const width = xRight - xLeft;
                    const height = yBottom - yTop;
                    // 如果交叉重叠区域宽度小于文字平均宽度，不进行对比
                    if (width < textBound.width / textElementBoxNode.textBox.text.length) continue;
                    // 如果交叉重叠区域宽度小于文字平均高度的一定比例，不进行对比
                    if (height < textBound.height / 10) continue;
                }
                const data = sortedRects[i].data as ITextColorBack;
                if (data.image || (data.color && data.color !== UnknownColor)) {
                    this.addTask(
                        textColor, data.color as Color,
                        textElementBoxNode, data,
                    );
                }
                break;
            }
        }
    }

    private getColorFromFill(fill: IFillProperty): Color | undefined | typeof UnknownColor {
        const fillType = fill.getShownFillType();
        if (fillType === undefined || fillType === FillType.NoFill) {
            return undefined;
        } if (fillType === FillType.SolidFill) {
            const color = (fill.getCurrentFill() as ISolidFillProperty)?.fillInfo.color;
            if (color?.alpha !== undefined && color.alpha < 0.8) return UnknownColor;
            return new Color((fill.getCurrentFill() as ISolidFillProperty)?.fillInfo.color.srgb_color);
        }
        return UnknownColor;
    }
}
